import React, { useEffect, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SaveIcon from "@mui/icons-material/Save";
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import {
  buscarLogs,
  buscarLogsPorId,
  deletarLog,
  salvarLogs,
} from "../../Services";
import { CircularProgress } from "@mui/material";
import moment from "moment";
import { useCotacao } from "../../hooks/useCotacao";

const EditableInput = ({
  label,
  items,
  selectedItem,
  setSelectedItem,
  disabled,
  loading,
  hasQuantidade = null,
  onSave = async () => {},
  labelNovo,
  onPaste = async () => {},
  onDelete = async () => {},
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [newItemName, setNewItemName] = useState("");
  const [quantidade, setQuantidade] = useState();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAddItem = async () => {
    if (newItemName.trim() === "") return;

    const newItem = {
      id: null,
      name: newItemName,
      quantidade: quantidade,
    };
    await onSave(newItem);
    setNewItemName("");
    setIsEditing(false);
  };

  const handleEditItem = async () => {
    if (selectedItem) {
      const editedItem = { ...selectedItem, name: newItemName, quantidade };
      await onSave(editedItem);
    }
    setIsEditing(false);
  };

  const handleDeleteItem = async () => {
    handleMenuClose();
    await onDelete(selectedItem);
  };

  const handleSetQuantidade = (e) => {
    setQuantidade(e.target.value);
  };

  const isExcelPaste = (clipboardData) => {
    return clipboardData.includes("\n");
  };

  const handlePaste = async (e) => {
    if (!hasQuantidade) {
      return;
    }

    const clipboardData = e.clipboardData.getData("text/plain");
    if (!isExcelPaste(clipboardData)) return;

    const rows = clipboardData.split("\n").map((row) => row.split("\t"));

    const newData = rows
      .map((row) => {
        const id = crypto.randomUUID();

        if (isNaN(row[1])) {
          return null;
        }

        return {
          id,
          name: row[0] || "",
          quantidade: Number(row[1]) || 0,
          itens: [],
        };
      })
      .filter((p) => !!p?.name);

    setNewItemName("");
    setQuantidade("");
    await onPaste(newData);

    setIsEditing(false);
  };

  useEffect(() => {
    if (hasQuantidade) {
      setQuantidade(selectedItem?.quantidade || 0);
    }
  }, [selectedItem]);

  return (
    <Box display="flex" alignItems="center" gap={1}>
      {isEditing ? (
        <TextField
          size="small"
          value={newItemName}
          onChange={(e) => setNewItemName(e.target.value)}
          label={selectedItem ? `Editar ${label}` : `Novo ${label}`}
          fullWidth
          onPaste={handlePaste}
          onKeyPress={(e) => {
            if (e.key === "Enter") {
              if (selectedItem) {
                handleEditItem();
              } else {
                handleAddItem();
              }
            }
          }}
        />
      ) : (
        <Autocomplete
          loadingText="Carregando informações..."
          size="small"
          loading={loading}
          disabled={disabled}
          fullWidth
          value={selectedItem}
          noOptionsText="Nenhuma opção encontrada"
          onChange={(event, newValue) => {
            setSelectedItem(newValue);
          }}
          options={items}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {option.quantidadeItens ? <CheckBoxIcon sx={{mr:1}} color="primary" /> : ""}
              {option.name}
            </li>
          )}
          renderInput={(params) => (
            <TextField {...params} label={label} fullWidth />
          )}
        />
      )}

      {hasQuantidade ? (
        <TextField
          key={selectedItem?.quantidade}
          size="small"
          label="Quantidade"
          value={quantidade}
          onChange={handleSetQuantidade}
          disabled={!isEditing}
        />
      ) : null}

      {loading ? (
        <CircularProgress size={18} style={{ padding: 12 }} />
      ) : isEditing ? (
        <IconButton
          onClick={() => {
            if (selectedItem) {
              handleEditItem();
            } else {
              handleAddItem();
            }
          }}
          color="primary"
        >
          <SaveIcon />
        </IconButton>
      ) : (
        <IconButton onClick={handleMenuOpen}>
          <MoreVertIcon />
        </IconButton>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            setIsEditing(true);
            setSelectedItem(null);
            setNewItemName("");
            setQuantidade("");
            handleMenuClose();
          }}
        >
          {labelNovo}
        </MenuItem>
        {!!selectedItem && (
          <MenuItem
            onClick={() => {
              setIsEditing(true);
              setNewItemName(selectedItem.name);
              handleMenuClose();
              setQuantidade(selectedItem?.quantidade || "");
            }}
          >
            Editar
          </MenuItem>
        )}
        {!!selectedItem && (
          <MenuItem onClick={handleDeleteItem}>Excluir</MenuItem>
        )}
      </Menu>
    </Box>
  );
};

const EditableAutocomplete = ({
  lista,
  setLista,
  setIdSelecionado = () => {},
  setTituloPesquisa = () => {},
}) => {
  const {
    cotacoes,
    itensCotacao,
    cotacaoSelecionada,
    itemSelecionado,
    setCotacaoSelecionada,
    setItemSelecionado,
    setCotacoes,
    setItensCotacao,
  } = useCotacao();

  const [loading, setLoading] = useState(false);

  const buscarCotacoes = async () => {
    const res = await buscarLogs();

    if (res.success) {
      if (res.data.length) {
        setCotacoes(res.data.map((p) => ({ id: p.id, name: p.titulo })));
      } else {
        setCotacoes([{ id: crypto.randomUUID(), name: "PESQUISA PADRÃO" }]);
      }
    }
  };

  const buscarCotacoesPorId = async () => {
    if (!cotacaoSelecionada?.id) return;
    setItemSelecionado(null);
    setLoading(true);
    const res = await buscarLogsPorId(cotacaoSelecionada?.id);
    setLoading(false);

    if (res.success) {
      const _itens = [];
      const pesquisasSalvas = [];

      for (let index = 0; index < res.data.data.pesquisas.length; index++) {
        const element = res.data.data.pesquisas[index];
        const id = crypto.randomUUID();

        const a = {
          id,
          name: element.nome,
          quantidade: element.quantidade,
          quantidadeItens: element?.itens?.length ?? 0,
        };

        const b = { ...element, id };

        _itens.push(a);
        pesquisasSalvas.push(b);
      }

      setLista(pesquisasSalvas);

      setItensCotacao(_itens);
    }
  };

  const handlePaste = async (elements = []) => {
    setLoading(true);
    const _id = cotacaoSelecionada?.id || null;

    const listaAtualizada = [...lista, ...elements].map((p) => ({
      ...p,
      nome: p.name || p.nome,
    }));
    const listaSalvar = listaAtualizada.map((p) => ({
      ...p,
      itens: p.itens.map((x) => x._id),
    }));

    const body = {
      titulo: cotacaoSelecionada?.name,
      itens: listaSalvar,
      date: moment(),
    };

    await salvarLogs(body, _id);
    setLoading(false);

    setItensCotacao(listaAtualizada.map((p) => ({ ...p, name: p.nome })));
    setLista(listaAtualizada);
  };

  const handleSaveCotacao = async (selectedItem) => {
    const _id = selectedItem?.id || null;

    const listaSalvar = _id
      ? lista.map((p) => ({ ...p, itens: p.itens.map((x) => x._id) }))
      : [];

    const body = {
      titulo: selectedItem?.name,
      itens: listaSalvar,
      date: moment(),
    };

    setLoading(true);
    const res = await salvarLogs(body, _id);
    setLoading(false);
    if (_id) {
      setCotacaoSelecionada(selectedItem);
      const updatedCotacoes = cotacoes.map((item) =>
        item.id === selectedItem.id
          ? { ...item, name: selectedItem.name }
          : item
      );
      setCotacoes(updatedCotacoes);
      setCotacoes(updatedCotacoes);
    } else {
      const newItem = { ...selectedItem, id: res.id };
      setCotacaoSelecionada(newItem);
      setCotacoes([newItem, ...cotacoes]);
    }

    setLista(body.itens);

    return res?.id || _id;
  };

  const handleSaveItem = async (selectedItem) => {
    setLoading(true);
    const id = crypto.randomUUID();
    const _lista = [...lista];

    const index = _lista.findIndex((p) => p.id == selectedItem?.id);

    if (index != -1) {
      _lista[index].nome = selectedItem?.name;
      _lista[index].quantidade = selectedItem?.quantidade || 0;

      setItemSelecionado(selectedItem);
      const newIitens = [...itensCotacao];
      newIitens[index] = { ...selectedItem };
      setItensCotacao(newIitens);
    } else {
      const newItem = {
        nome: selectedItem?.name,
        quantidade: selectedItem?.quantidade || 0,
        id,
        itens: [],
      };
      _lista.push(newItem);

      setItemSelecionado({ ...selectedItem, id });
      setItensCotacao([
        ...itensCotacao,
        { ...newItem, name: newItem.nome, quantidadeItens: 0 }
      ]);
    }

    const listaSalvar = _lista.map((p) => ({
      ...p,
      itens: p.itens.map((x) => x._id),
    }));

    const body = {
      titulo: cotacaoSelecionada?.name,
      itens: listaSalvar,
      date: moment(),
    };

    setLista(_lista);

    const _id = cotacaoSelecionada?.id || null;
    await salvarLogs(body, _id);
    // await buscarCotacoesPorId();
    setLoading(false);
  };

  const handleExcluirItems = async (selectedItem) => {
    setLoading(true);

    const listaAtualizada = lista.filter((p) => p.id != selectedItem.id);

    const listaSalvar = listaAtualizada.map((p) => ({
      ...p,
      itens: p.itens.map((x) => x._id),
    }));

    const body = {
      titulo: cotacaoSelecionada?.name,
      itens: listaSalvar,
      date: moment(),
    };

    await salvarLogs(body, cotacaoSelecionada?.id);

    setItensCotacao(listaAtualizada.map((p) => ({ ...p, name: p.nome })));
    setItemSelecionado(null);
    setLista(listaAtualizada);
    setLoading(false);
  };

  const handleExcluirCotacao = async (selectedItem) => {
    setLoading(true);

    await deletarLog(selectedItem.id);
    await buscarCotacoes();

    setCotacaoSelecionada(null);
    setLista([]);

    setLoading(false);
  };

  useEffect(() => {
    buscarCotacoes();
  }, []);

  useEffect(() => {
    sessionStorage.setItem(
      "titulo-cotacao",
      cotacaoSelecionada?.nome || cotacaoSelecionada?.name
    );
    setTituloPesquisa(cotacaoSelecionada);
    buscarCotacoesPorId();
  }, [cotacaoSelecionada]);

  useEffect(() => {
    setIdSelecionado(itemSelecionado?.id);
  }, [itemSelecionado?.id]);

  return (
    <Box sx={{ flexGrow: 1, paddingInline: 3, paddingBlock: 1, flex: 0 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={5}>
          <EditableInput
            label="Cotação"
            items={cotacoes}
            setItems={setCotacoes}
            selectedItem={cotacaoSelecionada}
            setSelectedItem={setCotacaoSelecionada}
            loading={loading}
            disabled={loading}
            onSave={handleSaveCotacao}
            labelNovo={"Nova cotação"}
            onDelete={handleExcluirCotacao}
          />
        </Grid>

        <Grid item xs={12} sm={7}>
          <EditableInput
            key={`${cotacaoSelecionada?.id}`}
            loading={loading}
            disabled={loading || !cotacaoSelecionada?.id}
            label="Item"
            items={itensCotacao}
            setItems={setItensCotacao}
            selectedItem={itemSelecionado}
            setSelectedItem={setItemSelecionado}
            hasQuantidade={true}
            onSave={handleSaveItem}
            labelNovo={"Novo item"}
            onPaste={handlePaste}
            onDelete={handleExcluirItems}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default EditableAutocomplete;
